import {
  CrossPlatformAbbreviations,
  IAdminConfiguration,
  PermissionsOfAdmin,
  ServiceTypes,
  convertToDecimals,
  currency,
  fallbackText,
} from '@pharmaplan/common';
import ServiceHandler from '@pharmaplan/common/helpers/ServiceHandler';
import { goToFallback } from '@pharmaplan/common/helpers/utils';
import {
  Constants,
  telephoneInputMask,
  TextFieldVariants,
} from '../../../../helpers/Constants';
import strings from '../../../../localization';
import { IInputModalConfig } from '../../../Modals/InputModal/InputModal';
import genericClasses from '../../../../theme/GenericClasses';

import styles from './styles';

const isSelfService = ServiceHandler.getService() === ServiceTypes.self;
const title = isSelfService ? strings.pharmagap : strings.pharmaPlan;

const keysToRemove = {
  pharmacyRate: 'pharmacyRate',
  pharmacistRate: 'pharmacistRate',
  techRate: 'techRate',
  salesForceKey: 'salesForceKey',
  ratePharmacy: 'ratePharmacy',
  crossPlatformKey: 'crossPlatformKey',
  studentRate: 'studentRate',
};

const {
  studentRate: studentRateKey,
  salesForceKey,
  ratePharmacy: ratePharmacyKey,
  crossPlatformKey,
} = keysToRemove;

export const handleChip = (value: boolean) =>
  ({
    chipStyle: value ? styles.bgPositive : styles.bgNegative,
    value: value ? strings.yes : strings.no,
  });

export const toProfessionalPharmacy = (
  professional: number | null | undefined,
  pharmacy: number | null | undefined,
) =>
  `${currency}${convertToDecimals(professional) || '-'} / $${convertToDecimals(pharmacy) || '-'}`;

export const settingsInfoConfig = (
  data: IAdminConfiguration,
  can: (permission: PermissionsOfAdmin) => boolean,
) => {
  const canEditCrossPlatformSettings = can(
    PermissionsOfAdmin.CrossPlatformSettings,
  );

  const {
    ratePharmacistDays,
    ratePharmacyDays = '',
    minHourWorkshift,
    pharmacistRate,
    pharmacyRate,
    representativeName,
    representativeEmail,
    representativePhone,
    representativePhoneExt,
    salesForceActive,
    salesForcePassword,
    salesForceSecurityToken,
    salesForceURL,
    salesForceUserName,
    envoiceBCCAdmin,
    technicianRate,
    showBookingDetails,
    bookingStateExpiration,
    pharmacyReminderBefore,
    enableChat,
    basicPackagePrice,
    unlimitedPackagePrice,
    tpsPercentage,
    tvqPercentage,
    taxOnTax,
    tpsNumber,
    tvqNumber,
    companyName,
    address,
    phoneNumber,
    city,
    province,
    pharmacyWeekDayHourlyRate = 0,
    pharmacyWeekendHourlyRate = 0,
    technicianWeekDayHourlyRate = 0,
    technicianWeekendHourlyRate = 0,
    pharmacistWeekDayHourlyRate = 0,
    pharmacistWeekendHourlyRate = 0,
    bookingInvitationConfiguration,
    sendBookingInvitationReminder,
    pharmacyWeekDayHourlyRateForTech,
    pharmacyWeekendHourlyRateForTech,
    pharmacyRateForTech,
    syncTechnicianShifts,
    syncPharmacistShifts,
  } = data ?? {};

  const {
    professionalCapacity,
    softwareKnowledge,
    preferredShiftHours,
    remoteRegions,
    maxTravelDistance,
    includePPShifts,
    availability,
    repeatAfterDays,
  } = bookingInvitationConfiguration ?? {};

  const commonContent = [
    {
      key: 0,
      label: strings.noOfDaysToRatePharmacist,
      value: ratePharmacistDays,
    },
    isSelfService && {
      key: ratePharmacyKey,
      label: strings.noOfDaysToRatePharmacy,
      value: ratePharmacyDays,
    },
    {
      key: 1,
      label: strings.minHoursWorkshift,
      value: minHourWorkshift,
    },
  ].filter(Boolean) as Array<{ key: string; label: string; value: any }>;

  const pharmacistRates = [
    {
      key: 'pharmacistSingleDayRates',
      xs: 4,
      label: strings.formatString(
        strings.singleDayFormat,
        `${strings.pharmacist}/${strings.pharmacy}`,
      ),
      value: toProfessionalPharmacy(pharmacistRate, pharmacyRate),
    },
    {
      key: 'pharmacistMultiWeekdayRates',
      xs: 4,
      label: strings.formatString(
        strings.multiWeekdayFormat,
        `${strings.pharmacist}/${strings.pharmacy}`,
      ),
      value: toProfessionalPharmacy(pharmacistWeekDayHourlyRate, pharmacyWeekDayHourlyRate),
    },
    {
      key: 'pharmacistMultiWeekendRates',
      xs: 4,
      label: strings.formatString(
        strings.multiWeekendFormat,
        `${strings.pharmacist}/${strings.pharmacy}`,
      ),
      value: toProfessionalPharmacy(pharmacistWeekendHourlyRate, pharmacyWeekendHourlyRate),
    },
  ];

  const technicianRates = [
    {
      key: 'technicianSingleDayRates',
      xs: 4,
      label: strings.formatString(
        strings.singleDayFormat,
        `${strings.technician}/${strings.pharmacy}`,
      ),
      value: toProfessionalPharmacy(technicianRate, pharmacyRateForTech),
    },
    {
      key: 'technicianMultiWeekdayRates',
      xs: 4,
      label: strings.formatString(
        strings.multiWeekdayFormat,
        `${strings.technician}/${strings.pharmacy}`,
      ),
      value: toProfessionalPharmacy(
        technicianWeekDayHourlyRate,
        pharmacyWeekDayHourlyRateForTech,
      ),
    },
    {
      key: 'technicianMultiWeekendRates',
      xs: 4,
      label: strings.formatString(
        strings.multiWeekendFormat,
        `${strings.technician}/${strings.pharmacy}`,
      ),
      value: toProfessionalPharmacy(
        technicianWeekendHourlyRate,
        pharmacyWeekendHourlyRateForTech,
      ),
    },
  ].filter(Boolean) as Array<{
    key: string;
    label: string;
    value: any;
    xs?: number;
  }>;

  const selfPreContent = [
    {
      key: '-1',
      label: strings.formatString(
        strings.feesFormat,
        strings.unlimited,
      ) as string,
      value: `$${convertToDecimals(unlimitedPackagePrice)}`,
    },
    {
      key: '-2',
      label: strings.formatString(
        strings.feesFormat,
        strings.standard,
      ) as string,
      value: `$${convertToDecimals(basicPackagePrice)}`,
    },
  ];

  const selfPostContent = [
    {
      key: '6',
      label: strings.requestPendingPeriod,
      value: `${bookingStateExpiration}${strings.hoursAbbreviation}`,
    },
    {
      key: '11',
      label: strings.pharmacyReminder,
      value: `${pharmacyReminderBefore}${strings.hoursAbbreviation}`,
    },
    {
      key: '12',
      label: strings.tps,
      value: `${tpsPercentage?.toFixed?.(3) ?? ''}%`,
    },
    {
      key: '13',
      label: strings.tvq,
      value: `${tvqPercentage?.toFixed?.(3) ?? ''}%`,
    },
    {
      key: '14',
      label: strings.taxOnTax,
      displayChip: true,
      ...handleChip(!!taxOnTax),
    },
  ];

  if (isSelfService) {
    commonContent.unshift(...selfPreContent);
    commonContent.push(...selfPostContent);
  }

  const pharmagapAccountSettings = {
    key: -1,
    title: strings.accountSettings,
    content: [
      {
        key: 0,
        label: strings.companyName,
        value: goToFallback(companyName, fallbackText),
      },
      {
        key: 1,
        label: strings.companyProvince,
        value: goToFallback(province, fallbackText),
      },
      {
        key: 2,
        label: strings.companyCity,
        value: goToFallback(city, fallbackText),
      },
      {
        key: 3,
        label: strings.companyAddress,
        value: goToFallback(address, fallbackText),
      },
      {
        key: 4,
        label: strings.companyPhone,
        value: goToFallback(phoneNumber, fallbackText),
      },
      isSelfService && {
        key: 5,
        label: strings.businessTPS,
        value: goToFallback(tpsNumber, fallbackText),
      },
      isSelfService && {
        key: 6,
        label: strings.businessTVQ,
        value: goToFallback(tvqNumber, fallbackText),
      },
    ].filter(Boolean) as Array<{
      key: number;
      label: string;
      value: string | number;
    }>,
  };

  let arr = [
    pharmagapAccountSettings,
    {
      key: 0,
      title: strings.formatString(strings.customSettings, title) as string,
      content: commonContent,
    },
    {
      key: 0.5,
      title: strings.hourlyRateSettings,
      subtitle: strings.pharmacist,
      content: pharmacistRates,
    },
    {
      key: 0.6,
      subtitle: strings.technician,
      content: technicianRates,
    },
    {
      key: 1,
      title: strings.formatString(
        strings.customSettings,
        strings.customerServiceRepresentative,
      ) as string,
      content: [
        {
          key: 0,
          label: strings.representativeName,
          value: representativeName,
        },
        {
          key: 1,
          label: strings.representativeEmail,
          value: representativeEmail,
        },
        {
          key: 2,
          label: strings.representativePhone,
          value: representativePhone,
        },
        {
          key: 3,
          label: strings.representativeExtPhone,
          value: representativePhoneExt,
        },
      ],
    },
    {
      key: salesForceKey,
      title: strings.formatString(
        strings.customSettings,
        strings.salesforce,
      ) as string,
      content: [
        {
          key: 0,
          label: strings.salesForceUsername,
          value: salesForceUserName,
        },
        {
          key: 1,
          label: strings.salesForcePWD,
          value: salesForcePassword,
          maskText: true,
        },
        {
          key: 2,
          label: strings.salesForceSecurityToken,
          value: salesForceSecurityToken,
          maskText: true,
        },
        {
          key: 3,
          label: strings.urlOfSalesForce,
          value: salesForceURL,
        },
        {
          key: 4,
          label: strings.integrationSalesForce,
          displayChip: true,
          ...handleChip(salesForceActive),
        },
      ],
    },
    {
      key: 1.5,
      title: strings.bookingInvitationEmailsSettings,
      content: [
        {
          key: 1,
          label: strings.professionalCapacity,
          displayChip: true,
          ...handleChip(professionalCapacity),
        },
        {
          key: 2,
          label: strings.softwareKnowledge,
          displayChip: true,
          ...handleChip(softwareKnowledge),
        },
        {
          key: 3,
          label: strings.preferredShiftDuration,
          displayChip: true,
          ...handleChip(preferredShiftHours),
        },
        {
          key: 4,
          label: strings.remoteRegions,
          displayChip: true,
          ...handleChip(remoteRegions),
        },
        {
          key: 4.5,
          label: strings.availability,
          displayChip: true,
          ...handleChip(availability),
        },
        {
          key: 5,
          label: strings.maxTravelDistance,
          displayChip: true,
          ...handleChip(maxTravelDistance),
        },
        isSelfService && {
          key: 7,
          label: strings.formatString(
            strings.showCrossShiftFormat,
            CrossPlatformAbbreviations.full,
            CrossPlatformAbbreviations.self,
          ),
          displayChip: true,
          ...handleChip(includePPShifts),
        },
        {
          key: 0,
          label: strings.sendBookingInvitationEmails,
          displayChip: true,
          ...handleChip(sendBookingInvitationReminder),
        },
        {
          key: 1.5,
          label: strings.bookingInvitationFrequencyDays,
          value: repeatAfterDays,
        },
      ].filter(Boolean) as Array<{
        key: number;
        label: string;
        value: string;
        displayChip: boolean;
      }>,
    },
    {
      key: 2,
      title: strings.formatString(
        strings.customSettings,
        strings.more,
      ) as string,
      content: [
        {
          key: 0,
          label: strings.sendAllEmailsBCC,
          displayChip: true,
          ...handleChip(envoiceBCCAdmin),
        },
        {
          key: 1,
          label: strings.showAllBookingDetails,
          displayChip: true,
          ...handleChip(showBookingDetails),
        },
        {
          key: 2,
          label: strings.enableChat,
          displayChip: true,
          ...handleChip(enableChat),
        },
      ],
    },
    {
      key: crossPlatformKey,
      title: strings.crossPlatformSettings,
      content: [
        {
          key: 0,
          label: strings.postTechnicianShiftsOnPG,
          displayChip: true,
          ...handleChip(syncTechnicianShifts ?? true),
        },
        {
          key: 1,
          label: strings.postPharmacistShiftsOnPG,
          displayChip: true,
          ...handleChip(syncPharmacistShifts ?? true),
        },
      ],
    },
  ];

  if (isSelfService) {
    arr = arr.filter((item) =>
      item.key !== salesForceKey);
  }

  if (isSelfService || !canEditCrossPlatformSettings) {
    arr = arr.filter((item) =>
      item.key !== crossPlatformKey);
  }

  return arr;
};

export const createNewForm: IInputModalConfig[] = [
  {
    key: '0',
    name: 'name',
    type: Constants.formInputType.text,
    variant: TextFieldVariants.filled,
    customTextFieldStyle: genericClasses.customFilledInput,
    gridLabel: strings.name,
    fieldXs: 8,
    labelXs: 4,
  },
  {
    key: '1',
    name: 'email',
    type: Constants.formInputType.text,
    variant: TextFieldVariants.filled,
    customTextFieldStyle: genericClasses.customFilledInput,
    gridLabel: strings.email,
    fieldXs: 8,
    labelXs: 4,
  },
  {
    key: '2',
    name: 'phone',
    gridLabel: strings.phone,
    variant: TextFieldVariants.filled,
    customTextFieldStyle: genericClasses.customFilledInput,
    fieldXs: 8,
    labelXs: 4,
    type: Constants.formInputType.masked,
    mask: telephoneInputMask,
    maxLength: 12,
  },
  {
    key: '3',
    name: 'received',
    type: Constants.formInputType.checkbox,
    gridLabel: strings.active,
    fieldXs: 8,
    labelXs: 4,
    customClass: genericClasses.checkboxContainer,
    customCheckboxStyle: genericClasses.checkboxStyle,
    gridLabelClass: styles.gridLabel,
  },
];
